import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  IconButton,
  InputAdornment,
  Modal,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import PhoneRoundedIcon from "@mui/icons-material/PhoneRounded";
import StickyNote2RoundedIcon from "@mui/icons-material/StickyNote2Rounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import { Button } from "components/Buttons";
import { callbackrequest, getallpackagelist } from "redux/actions";
import { useDispatch } from "react-redux";
import { message } from "components/Notifications";
import { useSelector } from "react-redux";

const buildInitialValues = (presetPackage) => ({
  contactNumber: "",
  username: "",
  notes: presetPackage?.package_name ? `Interested in booking: ${presetPackage.package_name}` : "",
  package: presetPackage?.id || "",
});

const fieldStyles = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "1rem",
    bgcolor: "#F8FFFD",
    "& fieldset": {
      borderColor: "#D8F3EB",
    },
    "&:hover fieldset": {
      borderColor: "#009D84",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#009D84",
    },
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "#009D84",
  },
};

const GetInTouchModal = ({ open, onClose, presetPackage }) => {
  const dispatch = useDispatch();
  const { standardallpackageList } = useSelector((state) => state.packages);
  const [values, setValues] = useState(() => buildInitialValues(presetPackage));
  const [errors, setErrors] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showAllPackages, setShowAllPackages] = useState(false);
  const visiblePackages = useMemo(() => {
    if (showAllPackages) return standardallpackageList || [];

    return standardallpackageList?.slice(0, 8) || [];
  }, [showAllPackages, standardallpackageList]);
  const hiddenPackageCount = Math.max(
    (standardallpackageList?.length || 0) - 8,
    0
  );

  const handleClose = () => {
    setValues(buildInitialValues(presetPackage));
    setErrors({});
    setIsSubmitted(false);
    setLoading(false);
    setShowAllPackages(false);
    onClose();
  };

  useEffect(() => {
    if (open) setValues(buildInitialValues(presetPackage));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, presetPackage]);

  // This modal is mounted once per package card, so fetching on mount fired
  // the package-list request N times per page. The list is only needed for
  // the picker, i.e. when the modal is actually open and has no preset
  // package — and only if it isn't already in the store.
  useEffect(() => {
    if (open && !presetPackage && !standardallpackageList?.length) {
      dispatch(getallpackagelist());
    }
  }, [open, presetPackage, standardallpackageList?.length, dispatch]);

  const handleChange = (event) => {
    const { name, value } = event.target;

    let nextValue = value;
    if (name === "username") {
      nextValue = value.replace(/[^a-zA-Z\s]/g, "");
    } else if (name === "contactNumber") {
      nextValue = value.replace(/\D/g, "").slice(0, 15);
    }

    setValues((prevValues) => ({
      ...prevValues,
      [name]: nextValue,
    }));

    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: "",
    }));
  };

  const validateForm = () => {
    const nextErrors = {};

    if (!values.username.trim()) {
      nextErrors.username = "Please enter your name";
    } else if (!/^[a-zA-Z\s]+$/.test(values.username.trim())) {
      nextErrors.username = "Name can only contain letters";
    }

    if (!values.contactNumber.trim()) {
      nextErrors.contactNumber = "Please enter your contact number";
    } else if (!/^\d+$/.test(values.contactNumber.trim())) {
      nextErrors.contactNumber = "Please enter a valid contact number";
    }

    if (!values.notes.trim()) {
      nextErrors.notes = "Please add a short note";
    }

    setErrors(nextErrors);

    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) return;

    try {
      setLoading(true);

      const payload = {
        contact_name: values.username,
        contact_number: values.contactNumber,
        note: values.notes,
        package: values.package || null,
      };

      const response = await callbackrequest(payload);

      if (response?.success) {
        handleClose();

        message("Callback request submitted successfully");
      }
    } catch (error) {
      console.error("Callback request failed:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="get-in-touch-title"
    >
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "90%", sm: 520 },
          maxHeight: "90vh",
          overflowY: "auto",
          bgcolor: "#fff",
          borderRadius: "1.5rem",
          boxShadow: "0 24px 70px rgba(0, 0, 0, 0.22)",
          outline: "none",
        }}
      >
        <Box
          sx={{
            position: "relative",
            p: { xs: "1.5rem", sm: "2rem" },
            borderRadius: "1.5rem 1.5rem 0 0",
            background:
              "linear-gradient(135deg, #D8F3EB 0%, #F3FFFB 55%, #FFFFFF 100%)",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              width: 170,
              height: 170,
              right: -60,
              top: -70,
              borderRadius: "50%",
              bgcolor: "rgba(0, 157, 132, 0.14)",
            }}
          />

          <IconButton
            onClick={handleClose}
            sx={{
              position: "absolute",
              right: "1rem",
              top: "1rem",
              bgcolor: "#fff",
              boxShadow: "0 8px 24px rgba(0, 0, 0, 0.08)",
              ":hover": {
                bgcolor: "#fff",
              },
            }}
          >
            <CloseRoundedIcon />
          </IconButton>

          <Typography
            id="get-in-touch-title"
            variant="h4"
            sx={{
              color: "#20242A",
              fontWeight: 700,
              pr: "3rem",
              mb: "0.75rem",
            }}
          >
            Get in Touch
          </Typography>

          <Typography
            sx={{
              color: "#4A5560",
              fontSize: "1rem",
              pr: "2rem",
            }}
          >
            Share your details and our HealOn Care Manager will connect with you
            for customised support.
          </Typography>
        </Box>

        <Stack spacing={2.25} sx={{ p: { xs: "1.5rem", sm: "2rem" } }}>
          {isSubmitted ? (
            <Stack alignItems="center" spacing={1.5} sx={{ py: "1rem" }}>
              <CheckCircleRoundedIcon
                sx={{
                  color: "#009D84",
                  fontSize: 58,
                }}
              />

              <Typography
                variant="h5"
                sx={{
                  color: "#20242A",
                  fontWeight: 700,
                  textAlign: "center",
                }}
              >
                Thank you!
              </Typography>

              <Typography
                sx={{
                  color: "#4A5560",
                  textAlign: "center",
                }}
              >
                Your request has been captured. Our team will contact you soon.
              </Typography>

              <Button
                type="button"
                variant="contained"
                onClick={handleClose}
                sx={{
                  mt: "0.75rem",
                  bgcolor: "#009D84",
                  borderRadius: "2rem",
                  px: "2rem",
                  ":hover": {
                    bgcolor: "#00816D",
                  },
                }}
              >
                Done
              </Button>
            </Stack>
          ) : (
            <>
              <TextField
                name="username"
                label="Full Name"
                placeholder="Enter your name"
                value={values.username}
                onChange={handleChange}
                error={!!errors.username}
                helperText={errors.username}
                autoComplete="name"
                fullWidth
                sx={fieldStyles}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonRoundedIcon sx={{ color: "#009D84" }} />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                name="contactNumber"
                label="Contact Number"
                placeholder="Enter your phone number"
                value={values.contactNumber}
                onChange={handleChange}
                error={!!errors.contactNumber}
                helperText={errors.contactNumber}
                fullWidth
                sx={fieldStyles}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneRoundedIcon sx={{ color: "#009D84" }} />
                    </InputAdornment>
                  ),
                }}
              />
              {presetPackage ? (
                <Box
                  sx={{
                    p: "0.85rem 1rem",
                    border: "1.5px solid #009D84",
                    borderRadius: "0.85rem",
                    bgcolor: "#F3FFFB",
                  }}
                >
                  <Typography sx={{ color: "#7A8793", fontSize: "0.8rem" }}>
                    Package
                  </Typography>
                  <Typography sx={{ color: "#20242A", fontWeight: 700 }}>
                    {presetPackage.package_name}
                  </Typography>
                </Box>
              ) : (
                <Box>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    spacing={1}
                    mb={1}
                  >
                    <Typography sx={{ color: "#20242A", fontWeight: 700 }}>
                      Select Package
                    </Typography>
                    {!!standardallpackageList?.length && (
                      <Typography sx={{ color: "#7A8793", fontSize: "0.8rem" }}>
                        Optional
                      </Typography>
                    )}
                  </Stack>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        sm: "repeat(2, minmax(0, 1fr))",
                      },
                      gap: 1,
                    }}
                  >
                    {visiblePackages?.map((pkg) => {
                      const isSelected = values.package === pkg.id;

                      return (
                        <Box
                          key={pkg.id}
                          onClick={() =>
                            setValues((prev) => ({
                              ...prev,
                              package: pkg.id,
                            }))
                          }
                          sx={{
                            cursor: "pointer",
                            minHeight: 58,
                            p: "0.85rem 1rem",
                            border: isSelected
                              ? "1.5px solid #009D84"
                              : "1px solid #D8F3EB",
                            borderRadius: "0.85rem",
                            bgcolor: isSelected ? "#F3FFFB" : "#FFFFFF",
                            boxShadow: isSelected
                              ? "0 10px 24px rgba(0, 157, 132, 0.12)"
                              : "0 6px 18px rgba(15, 23, 42, 0.04)",
                            transition:
                              "border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 1,
                            ":hover": {
                              borderColor: "#009D84",
                              boxShadow: "0 10px 24px rgba(0, 157, 132, 0.10)",
                              transform: "translateY(-1px)",
                            },
                          }}
                        >
                          <Typography
                            fontWeight={600}
                            sx={{
                              color: "#20242A",
                              fontSize: "0.9rem",
                              lineHeight: 1.25,
                            }}
                          >
                            {pkg.package_name}
                          </Typography>
                          {isSelected && (
                            <CheckCircleRoundedIcon
                              sx={{ color: "#009D84", fontSize: 20 }}
                            />
                          )}
                        </Box>
                      );
                    })}
                  </Box>

                  {hiddenPackageCount > 0 && (
                    <Button
                      type="button"
                      variant="outlined"
                      onClick={() => setShowAllPackages((prev) => !prev)}
                      sx={{
                        mt: 1.5,
                        width: "100%",
                        borderColor: "#D8F3EB",
                        color: "#009D84",
                        borderRadius: "1rem",
                        py: "0.7rem",
                        bgcolor: "#F8FFFD",
                        ":hover": {
                          borderColor: "#009D84",
                          bgcolor: "#F3FFFB",
                        },
                      }}
                      endIcon={
                        <ExpandMoreRoundedIcon
                          sx={{
                            transform: showAllPackages
                              ? "rotate(180deg)"
                              : "rotate(0deg)",
                            transition: "transform 0.2s ease",
                          }}
                        />
                      }
                    >
                      {showAllPackages
                        ? "Show less"
                        : `View more (${hiddenPackageCount})`}
                    </Button>
                  )}
                </Box>
              )}

              <TextField
                name="notes"
                label="Notes"
                placeholder="Tell us what you are looking for"
                value={values.notes}
                onChange={handleChange}
                error={!!errors.notes}
                helperText={errors.notes}
                fullWidth
                multiline
                minRows={4}
                sx={fieldStyles}
                InputProps={{
                  startAdornment: (
                    <InputAdornment
                      position="start"
                      sx={{
                        alignSelf: "flex-start",
                        mt: "0.5rem",
                      }}
                    >
                      <StickyNote2RoundedIcon sx={{ color: "#009D84" }} />
                    </InputAdornment>
                  ),
                }}
              />

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.5}
                justifyContent="flex-end"
              >
                <Button
                  type="button"
                  variant="outlined"
                  onClick={handleClose}
                  disabled={loading}
                  sx={{
                    borderColor: "#D8F3EB",
                    color: "#009D84",
                    borderRadius: "2rem",
                    px: "1.75rem",
                    ":hover": {
                      borderColor: "#009D84",
                      bgcolor: "#F3FFFB",
                    },
                  }}
                >
                  Cancel
                </Button>

                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                  sx={{
                    bgcolor: "#009D84",
                    borderRadius: "2rem",
                    px: "2rem",
                    boxShadow: "0 12px 28px rgba(0, 157, 132, 0.24)",
                    ":hover": {
                      bgcolor: "#00816D",
                    },
                  }}
                >
                  {loading ? "Submitting..." : "Submit Request"}
                </Button>
              </Stack>
            </>
          )}
        </Stack>
      </Box>
    </Modal>
  );
};

export default GetInTouchModal;
